/**
 * Kies Motorsports Bundle Builder
 * Multi-step wizard: vehicle → HP target → DME → platform → license → parts list
 */
(function () {
  'use strict';

  const STEP_LABELS = [
    'Vehicle',
    'Power target',
    'DME status',
    'Tuning platform',
    'License'
  ];

  const GROUP_LABELS = {
    requiredTuning: 'Required tuning',
    unlock: 'Unlock / service',
    hardware: 'Hardware',
    optional: 'Optional upgrades'
  };

  const root = document.getElementById('bundle-builder');
  if (!root) return;

  const els = {
    progressBar: root.querySelector('[data-progress-bar]'),
    steps: root.querySelector('[data-steps]'),
    panel: root.querySelector('[data-panel]'),
    back: root.querySelector('[data-back]'),
    next: root.querySelector('[data-next]'),
    summaryList: root.querySelector('[data-summary-list]'),
    summaryTotal: root.querySelector('[data-summary-total]'),
    results: root.querySelector('[data-results]'),
    resultsMeta: root.querySelector('[data-results-meta]'),
    recipeNotes: root.querySelector('[data-recipe-notes]'),
    partGroups: root.querySelector('[data-part-groups]'),
    startOver: root.querySelector('[data-start-over]'),
    addAll: root.querySelector('[data-add-all]'),
    epaModal: root.querySelector('[data-epa-modal]'),
    epaAgree: root.querySelector('[data-epa-agree]'),
    epaConfirm: root.querySelector('[data-epa-confirm]'),
    epaCancel: root.querySelector('[data-epa-cancel]'),
    toast: root.querySelector('[data-toast]')
  };

  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
  const cartUrl = root.dataset.cartUrl || '/cart';

  let data = loadJson('bundle-builder-data');
  const fallback = loadJson('bundle-builder-fallback');
  let usingFallback = false;

  if (!data.vehicles || data.vehicles.length === 0) {
    data = mergeFallback(data, fallback);
    usingFallback = true;
  }

  const state = {
    step: 0,
    vehicleId: null,
    powerTargetId: null,
    dmeUnlocked: null,
    dmeEra: 'post2020',
    platformSlug: null,
    hasLicense: null,
    resolvedParts: [],
    pendingCartAction: null
  };

  const urlParams = new URLSearchParams(window.location.search);
  const preselect = urlParams.get(data.settings?.preselectParam || 'vehicle');
  if (preselect) {
    const match = data.vehicles.find(
      (v) => v.chassisCode?.toLowerCase() === preselect.toLowerCase()
    );
    if (match) state.vehicleId = match.id;
  }

  bindEvents();
  render();

  function loadJson(id) {
    const el = document.getElementById(id);
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('Bundle builder: invalid JSON in #' + id, e);
      return {};
    }
  }

  function mergeFallback(primary, fb) {
    usingFallback = true;
    return {
      settings: primary.settings || {},
      vehicles: fb.vehicles || [],
      powerTargets: fb.powerTargets || [],
      platforms: fb.platforms || [],
      recipes: fb.recipes || [],
      productHandles: fb.productHandles || {}
    };
  }

  function bindEvents() {
    els.back.addEventListener('click', () => {
      if (state.step > 0) {
        state.step -= 1;
        render();
      }
    });

    els.next.addEventListener('click', async () => {
      if (!canProceed()) return;
      if (state.step < STEP_LABELS.length - 1) {
        state.step += 1;
        render();
        return;
      }
      await showResults();
    });

    els.startOver.addEventListener('click', () => {
      state.step = 0;
      state.powerTargetId = null;
      state.dmeUnlocked = null;
      state.platformSlug = null;
      state.hasLicense = null;
      state.resolvedParts = [];
      els.results.hidden = true;
      root.querySelector('.bb__layout').hidden = false;
      render();
    });

    els.addAll.addEventListener('click', () => {
      const items = collectCartItems();
      if (!items.length) {
        toast('No available items to add.');
        return;
      }
      queueCart(items);
    });

    els.epaAgree?.addEventListener('change', (e) => {
      els.epaConfirm.disabled = !e.target.checked;
    });

    els.epaCancel?.addEventListener('click', () => {
      els.epaModal.close();
      state.pendingCartAction = null;
    });

    els.epaModal?.querySelector('form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (state.pendingCartAction) {
        state.pendingCartAction();
        state.pendingCartAction = null;
      }
      els.epaModal.close();
    });

    els.partGroups.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-one]');
      if (!btn || btn.disabled) return;
      const card = btn.closest('[data-part-card]');
      const variantId = getCardVariantId(card);
      queueCart([{ id: variantId, quantity: 1 }]);
    });

    els.partGroups.addEventListener('change', (e) => {
      if (e.target.matches('[data-variant-select]')) {
        const card = e.target.closest('[data-part-card]');
        const opt = e.target.selectedOptions[0];
        card.dataset.variantId = opt.value;
        const priceEl = card.querySelector('[data-part-price]');
        if (priceEl) priceEl.textContent = formatMoney(Number(opt.dataset.price));
        updateSummaryTotal();
      }
      if (e.target.matches('[data-optional-toggle]')) {
        updateSummaryTotal();
      }
    });
  }

  function canProceed() {
    switch (state.step) {
      case 0:
        return !!state.vehicleId;
      case 1:
        return !!state.powerTargetId;
      case 2:
        return state.dmeUnlocked !== null;
      case 3:
        return !!state.platformSlug;
      case 4:
        return state.hasLicense !== null;
      default:
        return false;
    }
  }

  function render() {
    renderSteps();
    renderPanel();
    renderSummary();
    els.back.hidden = state.step === 0;
    els.next.textContent = state.step === STEP_LABELS.length - 1 ? 'View parts' : 'Next';
    els.next.disabled = !canProceed();
    const pct = ((state.step + 1) / STEP_LABELS.length) * 100;
    els.progressBar.style.width = pct + '%';
  }

  function renderSteps() {
    els.steps.innerHTML = STEP_LABELS.map((label, i) => {
      const cls = i === state.step ? 'bb__step bb__step--active' : i < state.step ? 'bb__step bb__step--done' : 'bb__step';
      return `<li class="${cls}"><span class="bb__step-num">${i + 1}</span><span class="bb__step-label">${label}</span></li>`;
    }).join('');
  }

  function renderPanel() {
    switch (state.step) {
      case 0:
        els.panel.innerHTML = renderVehicleStep();
        bindChoiceButtons(els.panel, 'vehicleId');
        break;
      case 1:
        els.panel.innerHTML = renderPowerStep();
        bindChoiceButtons(els.panel, 'powerTargetId');
        break;
      case 2:
        els.panel.innerHTML = renderDmeStep();
        bindChoiceButtons(els.panel, 'dmeUnlocked', (val) => val === 'true');
        bindChoiceButtons(els.panel, 'dmeEra', null, '[data-dme-era]');
        break;
      case 3:
        els.panel.innerHTML = renderPlatformStep();
        bindChoiceButtons(els.panel, 'platformSlug');
        break;
      case 4:
        els.panel.innerHTML = renderLicenseStep();
        bindChoiceButtons(els.panel, 'hasLicense', (val) => val === 'true');
        break;
    }
  }

  function renderVehicleStep() {
    const cards = data.vehicles.map((v) => {
      const selected = state.vehicleId === v.id ? ' bb__choice--selected' : '';
      const img = v.image
        ? `<img src="${escapeAttr(v.image)}" alt="" class="bb__choice-img" loading="lazy">`
        : `<div class="bb__choice-img bb__choice-img--placeholder">${escapeHtml(v.chassisCode?.toUpperCase() || '')}</div>`;
      return `
        <button type="button" class="bb__choice${selected}" data-value="${escapeAttr(v.id)}">
          ${img}
          <span class="bb__choice-title">${escapeHtml(v.name)}</span>
          <span class="bb__choice-meta">${escapeHtml(v.yearRange || '')} · ${escapeHtml(v.engine || '')}</span>
          <span class="bb__choice-hp">${v.stockHp} HP stock (crank)</span>
        </button>`;
    }).join('');
    return `<div class="bb__choices bb__choices--grid">${cards}</div>`;
  }

  function renderPowerStep() {
    const vehicle = getVehicle();
    const targets = data.powerTargets
      .filter((pt) => pt.vehicleId === state.vehicleId)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const stockBanner = vehicle
      ? `<p class="bb__banner">Stock <strong>${vehicle.stockHp} HP</strong> crank → choose your target</p>`
      : '';

    const cards = targets.map((pt) => {
      const selected = state.powerTargetId === pt.id ? ' bb__choice--selected' : '';
      return `
        <button type="button" class="bb__choice bb__choice--row${selected}" data-value="${escapeAttr(pt.id)}">
          <span class="bb__choice-title">${escapeHtml(pt.label)}</span>
          <span class="bb__choice-meta">${escapeHtml(pt.description || '')}</span>
          <span class="bb__choice-hp">+${Math.max(0, pt.targetHp - (vehicle?.stockHp || 0))} HP gain</span>
        </button>`;
    }).join('');

    return stockBanner + `<div class="bb__choices">${cards || '<p class="bb__empty">No power targets configured for this vehicle.</p>'}</div>`;
  }

  function renderDmeStep() {
    const yesSel = state.dmeUnlocked === true ? ' bb__choice--selected' : '';
    const noSel = state.dmeUnlocked === false ? ' bb__choice--selected' : '';
    const eraBlock = state.dmeUnlocked === false ? `
      <p class="bb__hint">When was your car produced?</p>
      <div class="bb__choices bb__choices--inline">
        <button type="button" class="bb__choice bb__choice--compact${state.dmeEra === 'pre2020' ? ' bb__choice--selected' : ''}" data-dme-era data-value="pre2020">Before June 2020</button>
        <button type="button" class="bb__choice bb__choice--compact${state.dmeEra === 'post2020' ? ' bb__choice--selected' : ''}" data-dme-era data-value="post2020">June 2020 or newer</button>
      </div>` : '';

    return `
      <p class="bb__question">Is your DME already unlocked for flash tuning?</p>
      <div class="bb__choices bb__choices--inline">
        <button type="button" class="bb__choice bb__choice--compact${yesSel}" data-value="true">Yes, unlocked</button>
        <button type="button" class="bb__choice bb__choice--compact${noSel}" data-value="false">No, still locked</button>
      </div>
      ${eraBlock}
      <p class="bb__fine-print">G80 M3 cars built after June 2020 require a bench or USA DME unlock before OBD flashing.</p>`;
  }

  function renderPlatformStep() {
    const platforms = data.platforms.filter((p) => {
      return true;
    });

    const cards = platforms.map((p) => {
      const selected = state.platformSlug === p.slug ? ' bb__choice--selected' : '';
      const logo = p.logo ? `<img src="${escapeAttr(p.logo)}" alt="" class="bb__choice-logo">` : '';
      return `
        <button type="button" class="bb__choice bb__choice--row${selected}" data-value="${escapeAttr(p.slug)}">
          ${logo}
          <span class="bb__choice-title">${escapeHtml(p.name)}</span>
        </button>`;
    }).join('');

    return `<div class="bb__choices">${cards}</div>`;
  }

  function renderLicenseStep() {
    const yesSel = state.hasLicense === true ? ' bb__choice--selected' : '';
    const noSel = state.hasLicense === false ? ' bb__choice--selected' : '';
    const platform = getPlatform();

    return `
      <p class="bb__question">Do you already own a ${escapeHtml(platform?.name || 'tuning')} license for this vehicle?</p>
      <div class="bb__choices bb__choices--inline">
        <button type="button" class="bb__choice bb__choice--compact${yesSel}" data-value="true">Yes, I have it</button>
        <button type="button" class="bb__choice bb__choice--compact${noSel}" data-value="false">No, include license</button>
      </div>`;
  }

  function bindChoiceButtons(container, key, transform, selector) {
    container.querySelectorAll(selector || '[data-value]').forEach((btn) => {
      if (selector === '[data-dme-era]' && !btn.hasAttribute('data-dme-era')) return;
      if (!selector && btn.hasAttribute('data-dme-era')) return;

      btn.addEventListener('click', () => {
        let val = btn.dataset.value;
        if (transform) val = transform(val);
        if (key === 'dmeEra') {
          state.dmeEra = val;
        } else {
          state[key] = val;
        }
        render();
      });
    });
  }

  function renderSummary() {
    const vehicle = getVehicle();
    const target = getPowerTarget();
    const platform = getPlatform();
    const rows = [];

    if (vehicle) {
      rows.push(['Vehicle', `${vehicle.name} (${vehicle.engine})`]);
      rows.push(['Stock HP', `${vehicle.stockHp} HP crank`]);
    }
    if (target) rows.push(['Target', `${target.label} (${target.targetHp} HP)`]);
    if (state.dmeUnlocked !== null) {
      rows.push(['DME', state.dmeUnlocked ? 'Unlocked' : 'Needs unlock']);
    }
    if (platform) rows.push(['Platform', platform.name]);
    if (state.hasLicense !== null) {
      rows.push(['License', state.hasLicense ? 'Already owned' : 'Include in build']);
    }

    els.summaryList.innerHTML = rows
      .map(([dt, dd]) => `<div class="bb__summary-row"><dt>${escapeHtml(dt)}</dt><dd>${escapeHtml(dd)}</dd></div>`)
      .join('');

    updateSummaryTotal();
  }

  function updateSummaryTotal() {
    if (!state.resolvedParts.length) {
      els.summaryTotal.hidden = true;
      return;
    }
    const total = state.resolvedParts
      .filter((p) => p.included !== false)
      .reduce((sum, p) => sum + (p.price || 0), 0);
    els.summaryTotal.hidden = false;
    els.summaryTotal.textContent = `Est. parts total: ${formatMoney(total)}`;
  }

  async function showResults() {
    const recipe = findRecipe();
    if (!recipe) {
      toast('No build recipe found for this combination. Contact Kies for help.');
      return;
    }

    let groups = recipe.groups || {};
    if (usingFallback && data.productHandles?.[recipe.handle]) {
      groups = await fetchGroupsFromHandles(data.productHandles[recipe.handle]);
    }

    const platform = getPlatform();
    state.resolvedParts = resolveParts(groups, platform, recipe);

    root.querySelector('.bb__layout').hidden = true;
    els.results.hidden = false;

    const vehicle = getVehicle();
    const target = getPowerTarget();
    els.resultsMeta.textContent = `${vehicle?.name || ''} · ${target?.label || ''} · ${platform?.name || ''}`;

    if (recipe.notes) {
      els.recipeNotes.hidden = false;
      els.recipeNotes.innerHTML = typeof recipe.notes === 'string' ? recipe.notes : '';
    } else {
      els.recipeNotes.hidden = true;
    }

    renderPartGroups(state.resolvedParts);
    updateSummaryTotal();
    els.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function findRecipe() {
    return data.recipes.find(
      (r) =>
        r.vehicleId === state.vehicleId &&
        r.powerTargetId === state.powerTargetId &&
        r.platformSlug === state.platformSlug
    );
  }

  function resolveParts(groups, platform, recipe) {
    const parts = [];
    const seen = new Set();

    const addProduct = (product, group, optional) => {
      if (!product || !product.variantId) return;
      const key = product.variantId + '-' + group;
      if (seen.has(key)) return;
      seen.add(key);
      parts.push({
        ...product,
        group,
        optional: !!optional,
        included: true
      });
    };

    (groups.requiredTuning || []).forEach((p) => addProduct(p, 'requiredTuning', false));
    (groups.hardware || []).forEach((p) => addProduct(p, 'hardware', false));
    (groups.optional || []).forEach((p) => addProduct(p, 'optional', true));

    if (state.dmeUnlocked === false && recipe.requiresDmeUnlock !== false) {
      const unlockList = groups.unlock || [];
      if (unlockList.length) {
        unlockList.forEach((p) => addProduct(p, 'unlock', false));
      } else if (platform) {
        const unlock =
          state.dmeEra === 'pre2020' ? platform.unlockPre2020 : platform.unlockPost2020;
        addProduct(unlock, 'unlock', false);
      }
    }

    if (state.hasLicense === false && recipe.includeLicense !== false && platform?.licenseProduct) {
      addProduct(platform.licenseProduct, 'requiredTuning', false);
    }

    if (state.hasLicense === true && platform?.licenseProduct) {
      const licenseHandle = platform.licenseProduct.handle;
      for (let i = parts.length - 1; i >= 0; i -= 1) {
        if (parts[i].handle === licenseHandle) parts.splice(i, 1);
      }
    }

    if (platform?.adapterProduct) {
      const inRequired = (groups.requiredTuning || []).some(
        (p) => p.handle === platform.adapterProduct.handle
      );
      if (!inRequired) {
        const already = parts.some((p) => p.handle === platform.adapterProduct.handle);
        if (!already) addProduct(platform.adapterProduct, 'requiredTuning', false);
      }
    }

    return parts;
  }

  async function fetchGroupsFromHandles(handleMap) {
    const groups = { requiredTuning: [], unlock: [], hardware: [], optional: [] };
    for (const [group, handles] of Object.entries(handleMap)) {
      for (const handle of handles) {
        const product = await fetchProduct(handle);
        if (product) groups[group]?.push(product);
      }
    }
    return groups;
  }

  async function fetchProduct(handle) {
    try {
      const res = await fetch(`/products/${handle}.json`);
      if (!res.ok) return null;
      const json = await res.json();
      const p = json.product;
      const v = p.variants[0];
      return {
        id: p.id,
        handle: p.handle,
        title: p.title,
        url: '/products/' + p.handle,
        featuredImage: p.image?.src || p.images?.[0]?.src || null,
        variantId: v.id,
        variantTitle: v.title,
        price: Math.round(parseFloat(v.price) * 100),
        available: v.available,
        variants: p.variants.map((vv) => ({
          id: vv.id,
          title: vv.title,
          price: Math.round(parseFloat(vv.price) * 100),
          available: vv.available
        }))
      };
    } catch (e) {
      console.warn('Failed to fetch product', handle, e);
      return null;
    }
  }

  function renderPartGroups(parts) {
    const byGroup = {};
    parts.forEach((p) => {
      if (!byGroup[p.group]) byGroup[p.group] = [];
      byGroup[p.group].push(p);
    });

    const order = ['requiredTuning', 'unlock', 'hardware', 'optional'];
    els.partGroups.innerHTML = order
      .filter((g) => byGroup[g]?.length)
      .map((g) => {
        const cards = byGroup[g].map((p) => renderPartCard(p, g === 'optional')).join('');
        return `
          <section class="bb__group">
            <h3 class="bb__group-title">${escapeHtml(GROUP_LABELS[g] || g)}</h3>
            <div class="bb__group-list">${cards}</div>
          </section>`;
      })
      .join('');
  }

  function renderPartCard(product, optional) {
    const img = product.featuredImage
      ? `<img src="${escapeAttr(product.featuredImage)}" alt="" loading="lazy" width="120" height="120">`
      : '<div class="bb-part__placeholder"></div>';

    const variantSelect =
      product.variants?.length > 1
        ? `<select class="bb-part__variant-select" data-variant-select>${product.variants
            .map(
              (v) =>
                `<option value="${v.id}" data-price="${v.price}" ${v.id === product.variantId ? 'selected' : ''} ${v.available ? '' : 'disabled'}>${escapeHtml(v.title)}${v.available ? '' : ' — Sold out'}</option>`
            )
            .join('')}</select>`
        : '';

    const toggle = optional
      ? `<label class="bb-part__toggle"><input type="checkbox" data-optional-toggle checked><span>Include</span></label>`
      : '';

    return `
      <article class="bb-part" data-part-card data-variant-id="${product.variantId}" data-handle="${escapeAttr(product.handle)}" data-optional="${optional}">
        <div class="bb-part__media">${img}</div>
        <div class="bb-part__body">
          <h4 class="bb-part__title"><a href="${escapeAttr(product.url)}" class="bb-part__link">${escapeHtml(product.title)}</a></h4>
          ${variantSelect}
          <p class="bb-part__price" data-part-price>${formatMoney(product.price)}</p>
        </div>
        <div class="bb-part__actions">
          ${toggle}
          <button type="button" class="bb__btn bb__btn--small bb__btn--ghost" data-add-one ${product.available ? '' : 'disabled'}>Add</button>
        </div>
      </article>`;
  }

  function collectCartItems() {
    const cards = els.partGroups.querySelectorAll('[data-part-card]');
    const items = [];
    cards.forEach((card) => {
      if (card.dataset.optional === 'true') {
        const toggle = card.querySelector('[data-optional-toggle]');
        if (toggle && !toggle.checked) return;
      }
      const variantId = getCardVariantId(card);
      const select = card.querySelector('[data-variant-select]');
      if (select) {
        const opt = select.selectedOptions[0];
        if (opt.disabled) return;
      } else if (card.querySelector('[data-add-one]')?.disabled) return;
      items.push({ id: Number(variantId), quantity: 1 });
    });
    return items;
  }

  function getCardVariantId(card) {
    const select = card.querySelector('[data-variant-select]');
    if (select) return Number(select.value);
    return Number(card.dataset.variantId);
  }

  function queueCart(items) {
    const run = () => performCartAdd(items);
    if (data.settings?.showEpaModal && els.epaModal) {
      state.pendingCartAction = run;
      els.epaAgree.checked = false;
      els.epaConfirm.disabled = true;
      els.epaModal.showModal();
    } else {
      run();
    }
  }

  async function performCartAdd(items) {
    els.addAll.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items })
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.description || body.message || 'Add to cart failed');

      toast(`Added ${items.length} item${items.length === 1 ? '' : 's'} to cart.`);

      document.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: body } })
      );

      if (data.settings?.redirectToCart) {
        window.location.href = cartUrl;
      }
    } catch (e) {
      toast(e.message || 'Could not add to cart.');
    } finally {
      els.addAll.disabled = false;
    }
  }

  function getVehicle() {
    return data.vehicles.find((v) => v.id === state.vehicleId);
  }

  function getPowerTarget() {
    return data.powerTargets.find((pt) => pt.id === state.powerTargetId);
  }

  function getPlatform() {
    return data.platforms.find((p) => p.slug === state.platformSlug);
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    const amount = (cents / 100).toFixed(2);
    return moneyFormat.replace('{{amount}}', amount).replace('{{ amount }}', amount);
  }

  function toast(message) {
    els.toast.textContent = message;
    els.toast.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(() => {
      els.toast.hidden = true;
    }, 4000);
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/'/g, '&#39;');
  }
})();
